import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { sendApiRequest } from '../utils/api';
import {
  FaPlus,
  FaMinus,
  FaClock,
  FaKeyboard,
  FaBook,
  FaCheckCircle,
  FaExclamationTriangle,
  FaPenFancy,
  FaBrain,
  FaLightbulb,
  FaChartLine,
  FaPalette,
  FaMagic,
  FaRocket,
  FaHourglassHalf,
  FaSpellCheck,
  FaWaveSquare,
  FaGlassWhiskey,
  FaTimes,
  FaCheck
} from 'react-icons/fa';
import Logo from "../assets/logo2.jpeg";

const ExamWriting = () => {
  const { id } = useParams();
  const location = useLocation();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [words, setWords] = useState(0);
  const [fontSize, setFontSize] = useState(18);
  const [input, setInput] = useState('');
  const [excludedCharViolations, setExcludedCharViolations] = useState([]);
  const [includedCharViolations, setIncludedCharViolations] = useState([]);
  const [keystrokes, setKeystrokes] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(0);
  const [showFontControls, setShowFontControls] = useState(true);
  const timerRef = useRef(null);
  const navigate = useNavigate();
  const [startTime, setStartTime] = useState(null);
  const textareaRef = useRef(null);
  const speedTimerRef = useRef(null);

  // Get data passed from PracticeTest
  const { customTime, selectedSpeed, selectedRule, topic: passedTopic } = location.state || {};

  useEffect(() => {
    const fetchTopic = async () => {
      try {
        // Use passed topic data if available, otherwise fetch
        if (passedTopic) {
          setTopic(passedTopic);
        } else {
          const response = await sendApiRequest(`api/admin/topics/${id}`, {}, 'GET');
          setTopic(response);
        }

        // Use custom time from PracticeTest or fallback to topic time
        const defaultTime = (passedTopic?.transcriptionTime || 1) * 60;
        const initialTime = customTime || defaultTime;

        setTimeLeft(initialTime);
        setStartTime(Date.now());
      } catch (err) {
        setError('Failed to load topic');
      } finally {
        setLoading(false);
      }
    };
    fetchTopic();
  }, [id, customTime, passedTopic]);

  useEffect(() => {
    if (!topic) return;
    if (timeLeft <= 0) return;

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [topic, timeLeft]);

  // Calculate typing speed in real-time
  useEffect(() => {
    if (input.length > 0 && startTime) {
      const timeElapsed = (Date.now() - startTime) / 1000 / 60; // in minutes
      const speed = Math.round((words / timeElapsed) || 0);
      setTypingSpeed(speed);
    }
  }, [input, words, startTime]);

  useEffect(() => {
    const text = input.trim();
    const wordCount = text ? text.split(/\s+/).length : 0;
    setWords(wordCount);

    // Check excluded characters
    const excludedChars = topic?.markingRule?.excludedCharacters?.split(', ').map(c => c.trim()) || [];
    const excludedViolations = [];
    if (excludedChars.length > 0) {
      for (let i = 0; i < text.length; i++) {
        if (excludedChars.includes(text[i])) {
          excludedViolations.push(i);
        }
      }
    }
    setExcludedCharViolations(excludedViolations);

    // Check included characters
    const includedChars = topic?.markingRule?.includedCharacters?.split(', ').map(c => c.trim()) || [];
    const includedViolations = [];
    if (includedChars.length > 0) {
      const charSet = new Set(text);
      for (let char of includedChars) {
        if (!charSet.has(char)) {
          includedViolations.push(char);
        }
      }
    }
    setIncludedCharViolations(includedViolations);
  }, [input, topic]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const getTimeColor = () => {
    if (timeLeft < 60) return 'text-red-600 animate-pulse';
    if (timeLeft < 300) return 'text-orange-500';
    return 'text-blue-600';
  };

  const getProgressPercentage = () => {
    const defaultTime = (topic?.transcriptionTime || 1) * 60;
    return Math.max(0, Math.min(100, ((defaultTime - timeLeft) / defaultTime) * 100));
  };

  // Calculate penalty based on custom marking rules
  const calculatePenalty = (mistakeType, customRules) => {
    if (!customRules || !customRules.isCustom) {
      // Default penalty for predefined rules
      return 1;
    }

    const rule = customRules.rules.find(r => r.mistakeName === mistakeType);
    if (rule) {
      return rule.penaltyValue || (rule.mistakeType === 'half' ? 0.5 : 1);
    }

    // Default penalty for unconfigured rules
    return 1;
  };

  // Handle auto-submit when time is up
  const handleAutoSubmit = async () => {
    if (input.trim().length === 0) return;

    const stats = compareText(input, topic.description);
    const endTime = Date.now();
    const actualTimeTakenSec = startTime ? Math.floor((endTime - startTime) / 1000) : (topic.transcriptionTime || 1) * 60;
    const actualTimeTakenMin = actualTimeTakenSec / 60;
    const dictationSpeed = actualTimeTakenMin > 0 ? Math.round(words / actualTimeTakenMin) : 0;

    try {
      await sendApiRequest(`api/tests/${id}/results`, {
        wordsPerMinute: dictationSpeed,
        accuracy: stats.accuracy,
        timeTaken: actualTimeTakenSec,
        mistakes: stats.totalMistake,
        typedText: input,
        referenceText: topic.description,
        audioUrl: topic.mp3File?.url || '',
        details: stats,
        punctuation: stats.punctuation || 0,
        date: new Date().toISOString(),
        hasExcludedCharViolations: stats.hasExcludedCharViolations,
        hasIncludedCharViolations: stats.hasIncludedCharViolations,
        excludedCharViolations: stats.excludedCharViolations,
        includedCharViolations: stats.includedCharViolations,
        selectedSpeed: selectedSpeed,
        selectedRule: selectedRule,
        customMarkingRules: selectedRule?.isCustom ? selectedRule.rules : null,
        totalPenalty: stats.totalPenalty,
        detailedPenalties: stats.detailedPenalties
      }, 'POST');
    } catch (err) {
      console.error('Failed to save result:', err);
    }


    navigate(`/exam-result/${id}`, {
      state: {
        stats: {
          ...stats,
          dictationSpeed,
          totalWords: stats.refWords.length,
          typedWords: stats.userWords.length,
          time: actualTimeTakenSec,
          date: new Date().toLocaleDateString(),
          selectedSpeed: selectedSpeed,
          selectedRule: selectedRule,
          customMarkingRules: selectedRule?.isCustom ? selectedRule.rules : null,
          totalPenalty: stats.totalPenalty,
          detailedPenalties: stats.detailedPenalties
        },
        userInput: input,
        reference: topic.description,
        audioUrl: topic.mp3File?.url || '',
        topicName: topic.title
      },
    });
  };

  function buildPenaltyMap(markingRule) {
    const map = {};

    if (!markingRule) return map;

    // ✅ CUSTOM RULE SUPPORT
    if (markingRule.isCustom && markingRule.rules) {
      markingRule.rules.forEach(rule => {
        // mistakeId matches comparison type
        map[rule.mistakeId] =
          rule.penaltyValue ??
          (rule.mistakeType === 'half' ? 0.5 : 1);
      });

      return map;
    }

    // ✅ PREDEFINED RULE SUPPORT (unchanged behavior)
    markingRule.fullMistakes?.forEach(m => {
      map[m.type] = m.penalty;
    });

    markingRule.halfMistakes?.forEach(m => {
      map[m.type] = m.penalty;
    });

    return map;
  }


  function compareTextSSC(userText, referenceText) {

    const hasSSCPunctuation = (word) => /[.?]/.test(word);

    const removeAllPunct = (text) =>
      text.replace(/[^\w]/g, '');

    // ✅ Levenshtein Distance
    function getEditDistance(a, b) {
      a = a.toLowerCase();
      b = b.toLowerCase();

      const matrix = Array(a.length + 1)
        .fill(null)
        .map(() => Array(b.length + 1).fill(0));

      for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
      for (let j = 0; j <= b.length; j++) matrix[0][j] = j;

      for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
          if (a[i - 1] === b[j - 1]) {
            matrix[i][j] = matrix[i - 1][j - 1];
          } else {
            matrix[i][j] = Math.min(
              matrix[i - 1][j] + 1,
              matrix[i][j - 1] + 1,
              matrix[i - 1][j - 1] + 1
            );
          }
        }
      }

      return matrix[a.length][b.length];
    }

    const normalize = t => t.replace(/\s+/g, ' ').trim();

    const userWords = normalize(userText).split(' ');
    const refWords = normalize(referenceText).split(' ');

    const penaltyMap = buildPenaltyMap(selectedRule);
    const getPenalty = (type) => penaltyMap[type] ?? 1;

    let i = 0, j = 0;

    let correct = 0;
    let mistake = 0;
    let missing = 0;
    let totalPenalty = 0;

    const comparison = [];

    const detailedPenalties = {
      spelling: { count: 0, penalty: 0 },
      missing: { count: 0, penalty: 0 },
      extra: { count: 0, penalty: 0 },
      capitalization: { count: 0, penalty: 0 },
      punctuation: { count: 0, penalty: 0 },
      substitution: { count: 0, penalty: 0 }
    };

    while (i < refWords.length && j < userWords.length) {

      const ref = refWords[i];
      const usr = userWords[j];

      // 1️⃣ Exact match
      if (ref === usr) {
        correct++;
        comparison.push({ type: 'correct', refWord: ref, userWord: usr });
        i++; j++;
        continue;
      }

      // 2️⃣ Capitalization
      if (ref.toLowerCase() === usr.toLowerCase()) {
        const p = getPenalty('capitalization');
        totalPenalty += p;
        detailedPenalties.capitalization.count++;
        detailedPenalties.capitalization.penalty += p;

        comparison.push({ type: 'capitalization', refWord: ref, userWord: usr });
        i++; j++;
        continue;
      }

      // 3️⃣ Reference has hyphen/slash → user split
      if (j + 1 < userWords.length) {

        const refClean = removeAllPunct(ref).toLowerCase();
        const usrCombined = removeAllPunct(userWords[j] + userWords[j + 1]).toLowerCase();

        if (
          refClean === usrCombined &&
          (ref.includes('-') || ref.includes('/'))
        ) {
          comparison.push({
            type: 'correct',
            refWord: ref,
            userWord: userWords[j] + ' ' + userWords[j + 1]
          });

          correct++;
          i++;
          j += 2;
          continue;
        }
      }

      // 4️⃣ User has hyphen/slash → reference split
      if (i + 1 < refWords.length) {

        const refCombined = removeAllPunct(refWords[i] + refWords[i + 1]).toLowerCase();
        const usrClean = removeAllPunct(usr).toLowerCase();

        if (
          refCombined === usrClean &&
          (usr.includes('-') || usr.includes('/'))
        ) {
          comparison.push({
            type: 'correct',
            refWord: refWords[i] + ' ' + refWords[i + 1],
            userWord: usr
          });

          correct++;
          i += 2;
          j++;
          continue;
        }
      }

      const refNoPunct = removeAllPunct(ref).toLowerCase();
      const usrNoPunct = removeAllPunct(usr).toLowerCase();

      // 5️⃣ Same after removing punctuation
      if (refNoPunct === usrNoPunct) {

        const refHasDotQ = hasSSCPunctuation(ref);
        const usrHasDotQ = hasSSCPunctuation(usr);

        if (refHasDotQ !== usrHasDotQ) {
          const p = getPenalty('punctuation');
          totalPenalty += p;
          detailedPenalties.punctuation.count++;
          detailedPenalties.punctuation.penalty += p;

          comparison.push({ type: 'punctuation', refWord: ref, userWord: usr });
        } else {
          correct++;
          comparison.push({ type: 'correct', refWord: ref, userWord: usr });
        }

        i++; j++;
        continue;
      }

      // 6️⃣ Missing
      if (
        i + 1 < refWords.length &&
        removeAllPunct(refWords[i + 1]).toLowerCase() === usrNoPunct
      ) {
        const p = getPenalty('missing');
        totalPenalty += p;
        detailedPenalties.missing.count++;
        detailedPenalties.missing.penalty += p;

        comparison.push({ type: 'missing', refWord: ref, userWord: null });
        missing++;
        i++;
        continue;
      }

      // 7️⃣ Extra
      if (
        j + 1 < userWords.length &&
        refNoPunct === removeAllPunct(userWords[j + 1]).toLowerCase()
      ) {
        const p = getPenalty('extra');
        totalPenalty += p;
        detailedPenalties.extra.count++;
        detailedPenalties.extra.penalty += p;

        comparison.push({ type: 'extra', refWord: null, userWord: usr });
        mistake++;
        j++;
        continue;
      }

      // 8️⃣ Spelling vs Substitution (Improved Logic)
      const distance = getEditDistance(refNoPunct, usrNoPunct);

      if (distance > 0 && distance <= 2) {
        const p = getPenalty('spelling');
        totalPenalty += p;
        detailedPenalties.spelling.count++;
        detailedPenalties.spelling.penalty += p;

        comparison.push({ type: 'incorrect', refWord: ref, userWord: usr });
      } else {
        const p = getPenalty('substitution');
        totalPenalty += p;
        detailedPenalties.substitution.count++;
        detailedPenalties.substitution.penalty += p;

        comparison.push({ type: 'substitution', refWord: ref, userWord: usr });
      }

      mistake++;
      i++; j++;
    }

    while (i < refWords.length) {
      const p = getPenalty('missing');
      totalPenalty += p;
      detailedPenalties.missing.count++;
      detailedPenalties.missing.penalty += p;

      comparison.push({ type: 'missing', refWord: refWords[i], userWord: null });
      missing++;
      i++;
    }

    while (j < userWords.length) {
      const p = getPenalty('extra');
      totalPenalty += p;
      detailedPenalties.extra.count++;
      detailedPenalties.extra.penalty += p;

      comparison.push({ type: 'extra', refWord: null, userWord: userWords[j] });
      mistake++;
      j++;
    }

    const totalMistake =
      detailedPenalties.spelling.count +
      detailedPenalties.missing.count +
      detailedPenalties.extra.count +
      detailedPenalties.capitalization.count +
      detailedPenalties.punctuation.count;

    const accuracy = Math.max(
      0,
      Number((((refWords.length - totalMistake) / refWords.length) * 100).toFixed(2))
    );

    return {
      correct,
      mistake,
      missing,
      totalMistake,
      accuracy,
      totalPenalty,
      detailedPenalties,
      comparison,
      userWords,
      refWords
    };
  }




  function compareText(userText, referenceText) {
    const normalize = t => t.replace(/\s+/g, ' ').trim();
    const isSSC = selectedRule?.title?.toLowerCase().includes('ssc');
    if (isSSC) {
      return compareTextSSC(userText, referenceText);
    }
    const removePunctuation = w =>
      w.replace(/[.,\/#!$%^&*;:{}=_`~()-]/g, '');
    const lowerNoPunc = w => removePunctuation(w).toLowerCase();
    const hasPunctuation = (word) => /[.,\/#!$%^&*;:{}=_`~()]/.test(word);
    function isMinorSpelling(ref, usr) {
      const a = lowerNoPunc(ref);
      const b = lowerNoPunc(usr);

      if (a === b) return false;
      if (a.length < 3 || b.length < 3) return false;

      let diff = 0;
      let i = 0, j = 0;

      while (i < a.length && j < b.length) {
        if (a[i] !== b[j]) {
          diff++;
          if (diff > 1) return false;
          if (a.length > b.length) i++;
          else if (b.length > a.length) j++;
          else {
            i++;
            j++;
          }
        } else {
          i++;
          j++;
        }
      }

      diff += Math.abs(a.length - b.length);
      return diff === 1;
    }



    const userWords = normalize(userText).split(' ');
    const refWords = normalize(referenceText).split(' ');

    const penaltyMap = buildPenaltyMap(selectedRule);
    const getPenalty = (type) => penaltyMap[type] ?? 1;

    let i = 0, j = 0;

    let correct = 0;
    let mistake = 0;
    let missing = 0;
    let totalPenalty = 0;

    const comparison = [];

    const detailedPenalties = {
      spelling: { count: 0, penalty: 0 },
      missing: { count: 0, penalty: 0 },
      extra: { count: 0, penalty: 0 },
      capitalization: { count: 0, penalty: 0 },
      punctuation: { count: 0, penalty: 0 },
      substitution: { count: 0, penalty: 0 }
    };

    while (i < refWords.length && j < userWords.length) {
      const ref = refWords[i];
      const usr = userWords[j];

      // 1. Exact match (perfect)
      if (ref === usr) {
        correct++;
        comparison.push({ type: 'correct', refWord: ref, userWord: usr });
        i++;
        j++;
        continue;
      }

      // 🔧 SPECIAL FIX: slash split like "letters/paper" vs "letters paper"
      if (
        i < refWords.length &&
        j + 1 < userWords.length
      ) {
        const refClean = lowerNoPunc(refWords[i]); // "letters/paper" → "letterspaper"
        const usrClean = lowerNoPunc(userWords[j] + userWords[j + 1]); // "letters" + "paper"

        if (
          refClean === usrClean &&
          refWords[i].includes('/')
        ) {
          const p = getPenalty('punctuation');
          totalPenalty += p;

          detailedPenalties.punctuation.count++;
          detailedPenalties.punctuation.penalty += p;

          comparison.push({
            type: 'punctuation',
            refWord: refWords[i],
            userWord: userWords[j] + ' ' + userWords[j + 1]
          });

          // 🔑 ALIGNMENT FIX
          i++;
          j += 2;
          continue;
        }
      }


      // 🔧 SPECIAL FIX: numeric slash split like "342/321," vs "342 321,"
      if (
        i < refWords.length &&
        j + 1 < userWords.length
      ) {
        const refCombined = lowerNoPunc(refWords[i]); // "342/321," → "342321"
        const usrCombined = lowerNoPunc(userWords[j] + userWords[j + 1]); // "342" + "321," → "342321"

        // Ensure this is numeric-ish case (avoid normal words)
        if (
          refCombined === usrCombined &&
          /\d/.test(refCombined)
        ) {
          const p = getPenalty('punctuation');
          totalPenalty += p;

          detailedPenalties.punctuation.count++;
          detailedPenalties.punctuation.penalty += p;

          comparison.push({
            type: 'punctuation',
            refWord: refWords[i],
            userWord: userWords[j] + ' ' + userWords[j + 1]
          });

          // 🔑 FIX ALIGNMENT
          i++;
          j += 2;
          continue;
        }
      }

      // 🔧 SPECIAL FIX: hyphen split like "two-sanction" vs "two sanction"
      if (
        i < refWords.length &&
        j + 1 < userWords.length
      ) {
        const refClean = lowerNoPunc(refWords[i]);
        const usrClean = lowerNoPunc(userWords[j] + userWords[j + 1]);

        if (
          refClean === usrClean &&
          refWords[i].includes('-')
        ) {
          const p = getPenalty('punctuation');
          totalPenalty += p;

          detailedPenalties.punctuation.count++;
          detailedPenalties.punctuation.penalty += p;

          comparison.push({
            type: 'punctuation',
            refWord: refWords[i],
            userWord: userWords[j] + ' ' + userWords[j + 1]
          });

          i++;
          j += 2;
          continue;
        }
      }



      // 2. Content match after removing punctuation and ignoring case
      const refLowerNoPunc = lowerNoPunc(ref);
      const usrLowerNoPunc = lowerNoPunc(usr);

      if (refLowerNoPunc === usrLowerNoPunc) {
        // अब देखते हैं कि difference किस तरह का है

        // Case A: सिर्फ punctuation अलग है (case same)
        const refNoPunc = removePunctuation(ref);
        const usrNoPunc = removePunctuation(usr);

        if (refNoPunc === usrNoPunc) {
          // Pure punctuation difference
          const p = getPenalty('punctuation');
          totalPenalty += p;
          detailedPenalties.punctuation.count++;
          detailedPenalties.punctuation.penalty += p;

          comparison.push({ type: 'punctuation', refWord: ref, userWord: usr });
          i++;
          j++;
          continue;
        }

        // Case B: Case अलग है (capitalization difference)
        // अगर यहाँ तक पहुँचे हैं तो case अलग है (क्योंकि ऊपर का if fail हुआ)
        const p = getPenalty('capitalization');
        totalPenalty += p;
        detailedPenalties.capitalization.count++;
        detailedPenalties.capitalization.penalty += p;

        comparison.push({ type: 'capitalization', refWord: ref, userWord: usr });
        i++;
        j++;
        continue;
      }

      // 3. Single spelling error with alignment check (lookahead)
      // 3. Single spelling error with alignment check (lookahead)
      // 🔥 BUT avoid word substitution like "The" → "If"
      if (
        i + 1 < refWords.length &&
        j + 1 < userWords.length &&
        lowerNoPunc(refWords[i + 1]) === lowerNoPunc(userWords[j + 1]) &&
        (
          // spelling-like words only
          Math.abs(
            lowerNoPunc(ref).length - lowerNoPunc(usr).length
          ) <= 1 &&
          (
            lowerNoPunc(ref).includes(lowerNoPunc(usr)) ||
            lowerNoPunc(usr).includes(lowerNoPunc(ref))
          )
        )
      ) {

        const p = getPenalty('spelling');
        totalPenalty += p;
        detailedPenalties.spelling.count++;
        detailedPenalties.spelling.penalty += p;

        comparison.push({
          type: 'incorrect',
          refWord: ref,
          userWord: usr
        });

        mistake++;
        i++;
        j++;
        continue;
      }

      // 4. Missing word (omission)
      if (
        i + 1 < refWords.length &&
        lowerNoPunc(refWords[i + 1]) === lowerNoPunc(usr)
      ) {
        // word omission
        const pWord = getPenalty('missing');
        totalPenalty += pWord;
        detailedPenalties.missing.count++;
        detailedPenalties.missing.penalty += pWord;

        comparison.push({
          type: 'missing',
          refWord: ref,
          userWord: null
        });

        missing++;

        // 🔥 IMPORTANT: punctuation omission check
        if (hasPunctuation(ref)) {
          const pPunc = getPenalty('punctuation');
          totalPenalty += pPunc;
          detailedPenalties.punctuation.count++;
          detailedPenalties.punctuation.penalty += pPunc;
        }

        i++;
        continue;
      }


      // 5. Extra word (insertion)
      if (
        j + 1 < userWords.length &&
        lowerNoPunc(ref) === lowerNoPunc(userWords[j + 1])
      ) {
        const p = getPenalty('extra');
        totalPenalty += p;
        detailedPenalties.extra.count++;
        detailedPenalties.extra.penalty += p;

        comparison.push({
          type: 'extra',
          refWord: null,
          userWord: usr
        });

        mistake++;
        j++;
        continue;
      }

      // 5.5 MULTI-WORD OMISSION LOOKAHEAD (VERY IMPORTANT)
      let foundAhead = -1;

      for (let k = i + 1; k < refWords.length; k++) {
        if (lowerNoPunc(refWords[k]) === lowerNoPunc(usr)) {
          foundAhead = k;
          break;
        }
      }

      if (foundAhead !== -1) {
        // mark all skipped reference words as omissions
        while (i < foundAhead) {
          const refMiss = refWords[i];

          const p = getPenalty('missing');
          totalPenalty += p;

          detailedPenalties.missing.count++;
          detailedPenalties.missing.penalty += p;

          comparison.push({
            type: 'missing',
            refWord: refMiss,
            userWord: null
          });

          // punctuation inside omitted words
          if (hasPunctuation(refMiss)) {
            const pP = getPenalty('punctuation');
            totalPenalty += pP;
            detailedPenalties.punctuation.count++;
            detailedPenalties.punctuation.penalty += pP;
          }

          missing++;
          i++;
        }

        // now current user word matches
        comparison.push({
          type: 'correct',
          refWord: refWords[i],
          userWord: usr
        });

        correct++;
        i++;
        j++;
        continue;
      }


      // 6. Fallback → spelling OR substitution (COURT RULE COMPLIANT)

      // normalize for comparison
      const refNorm = lowerNoPunc(ref);
      const usrNorm = lowerNoPunc(usr);

      // very strict spelling condition
      const isSpelling = isMinorSpelling(ref, usr);


      if (isSpelling) {
        // ✅ SPELLING (teh → the)
        const p = getPenalty('spelling');
        totalPenalty += p;

        detailedPenalties.spelling.count++;
        detailedPenalties.spelling.penalty += p;

        comparison.push({
          type: 'incorrect',
          refWord: ref,
          userWord: usr
        });
      } else {
        // ✅ SUBSTITUTION (the → if)
        const p = getPenalty('substitution') ?? getPenalty('spelling');
        totalPenalty += p;

        detailedPenalties.substitution.count++;
        detailedPenalties.substitution.penalty += p;

        comparison.push({
          type: 'substitution',
          refWord: ref,
          userWord: usr
        });
      }

      mistake++;
      i++;
      j++;
      continue;


    }

    // Remaining reference words → missing
    while (i < refWords.length) {
      const ref = refWords[i];

      const pWord = getPenalty('missing');
      totalPenalty += pWord;
      detailedPenalties.missing.count++;
      detailedPenalties.missing.penalty += pWord;

      comparison.push({ type: 'missing', refWord: ref, userWord: null });
      missing++;

      // 🔥 punctuation check
      if (hasPunctuation(ref)) {
        const pPunc = getPenalty('punctuation');
        totalPenalty += pPunc;
        detailedPenalties.punctuation.count++;
        detailedPenalties.punctuation.penalty += pPunc;
      }

      i++;
    }


    // Remaining user words → extra
    while (j < userWords.length) {
      const p = getPenalty('extra');
      totalPenalty += p;
      detailedPenalties.extra.count++;
      detailedPenalties.extra.penalty += p;

      comparison.push({ type: 'extra', refWord: null, userWord: userWords[j] });
      mistake++;
      j++;
    }

    // Total mistakes (अब punctuation को भी accuracy में शामिल कर सकते हैं या नहीं - आपकी जरूरत पर निर्भर)
    const totalMistake =
      detailedPenalties.spelling.count +
      detailedPenalties.missing.count +
      detailedPenalties.extra.count +
      detailedPenalties.capitalization.count +
      detailedPenalties.punctuation.count; // ← अब punctuation भी शामिल

    const accuracy = Math.max(
      0,
      Number((((refWords.length - totalMistake) / refWords.length) * 100).toFixed(2))
    );

    return {
      correct,
      mistake,
      missing,
      totalMistake,
      accuracy,
      totalPenalty,
      detailedPenalties,
      comparison,
      userWords,
      refWords
    };
  }



  const calculateKPM = () => {
    const timeTaken = timeLeft / 60;
    return Math.round(keystrokes / timeTaken);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="relative">
            <div className="w-24 h-24 border-4 border-transparent border-t-blue-500 border-r-blue-300 rounded-full animate-spin mx-auto mb-6"></div>
            <FaPenFancy className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-blue-500 text-2xl" />
          </div>
          <p className="text-xl text-gray-700 font-medium mt-4">Preparing your exam...</p>
          <p className="text-gray-500 mt-2">Loading all the tools</p>
        </div>
      </div>
    );
  }

  if (error || !topic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center bg-white p-10 rounded-2xl shadow-xl max-w-md border border-gray-200">
          <div className="relative">
            <FaExclamationTriangle className="text-red-500 text-6xl mx-auto mb-6" />
            <div className="absolute -inset-4 bg-red-100 rounded-full"></div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-3">Oops!</h2>
          <p className="text-gray-600 mb-6 text-lg">{error || 'Topic not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="group relative bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
          >
            <span className="relative z-10">Return Home</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1d3a80] font-[montserrat]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}

        <div className="px-2 py-3 ">
          <div className="max-w-3xl mx-auto flex flex-col items-center text-center text-white">

            {/* Logo */}
            <div className="w-28 h-28 rounded-full bg-white flex items-center justify-center shadow">
              <img
                src={Logo}
                alt="Logo"
                className="w-20 h-20 object-contain"
              />
            </div>

            {/* Text Content */}
            <div>
              <h1 className="text-xl font-bold">
                Shorthand Software Academy
              </h1>
              <p className="text-sm text-white/90 ">
                shorthandsoftwareacademy@gmail.com
              </p>
              <p className="text-sm text-white/90">
                +91 87872 24408
              </p>
            </div>

          </div>
        </div>


        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2 px-4">
          {[
            {
              title: "Time Remaining",
              value: formatTime(timeLeft),
              color: "blue",
              icon: FaClock,
              textColor: getTimeColor()
            },
            {
              title: "Words Typed",
              value: words,
              color: "green",
              icon: FaKeyboard,
              textColor: "text-green-600"
            },
            {
              title: "Typing Speed",
              value: `${typingSpeed} WPM`,
              color: "purple",
              icon: FaRocket,
              textColor: "text-purple-600"
            },
            {
              title: "Keystrokes",
              value: keystrokes,
              color: "orange",
              icon: FaChartLine,
              textColor: "text-orange-600"
            }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium mb-1">{stat.title}</p>
                  <p className={`text-3xl font-bold ${stat.textColor} font-mono`}>{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg bg-${stat.color}-50`}>
                  <stat.icon className={`text-${stat.color}-500 text-2xl`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Writing Area Container */}
        <div className="bg-white rounded-2xl shadow-lg p-2 mb-6 border border-gray-200">

          {/* Font Style Controls - Top Left */}
          {showFontControls && (
            <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setFontSize(f => Math.max(f - 2, 14))}
                  className="w-10 h-10 rounded-lg bg-white border border-gray-300 hover:border-blue-500 hover:bg-blue-50 flex items-center justify-center transition-all duration-200"
                  aria-label="Decrease font size"
                >
                  <FaMinus className="text-gray-600 hover:text-blue-600" />
                </button>

                <div className="flex-1">
                  <input
                    type="range"
                    min="14"
                    max="32"
                    step="2"
                    value={fontSize}
                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-lg"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>14px</span>
                    <span>22px</span>
                    <span>32px</span>
                  </div>
                </div>

                <button
                  onClick={() => setFontSize(f => Math.min(f + 2, 32))}
                  className="w-10 h-10 rounded-lg bg-white border border-gray-300 hover:border-blue-500 hover:bg-blue-50 flex items-center justify-center transition-all duration-200"
                  aria-label="Increase font size"
                >
                  <FaPlus className="text-gray-600 hover:text-blue-600" />
                </button>
              </div>

            </div>
          )}

          {/* Main Writing Area */}
          <div className="relative rounded-xl overflow-hidden border-2 border-gray-300 hover:border-blue-400 transition-colors duration-300">
            <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
            </div>

            {/* Textarea */}
            <textarea
              ref={textareaRef}
              className={`w-full h-[700px] rounded-xl p-6 pt-12 text-gray-800 focus:outline-none resize-none font-sans bg-white placeholder:text-gray-400 ${isFocused ? 'ring-2 ring-blue-500/30' : ''
                }`}
              style={{
                fontSize: `${fontSize}px`,
                lineHeight: '1.8',
                letterSpacing: '0.01em',
                fontFamily: "'Inter', system-ui, sans-serif"
              }}
              placeholder="Start typing your text here... Be precise with spelling, punctuation, and capitalization."
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setKeystrokes(prev => prev + 1);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              disabled={timeLeft === 0}
              autoFocus
            />

            {/* Corner Accents */}
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-blue-200 rounded-tl-xl"></div>
            <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-blue-200 rounded-tr-xl"></div>
          </div>

          {/* Character Rules Warning */}
          {(excludedCharViolations.length > 0 || includedCharViolations.length > 0) && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-100">
                  <FaExclamationTriangle className="text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-yellow-800 mb-1">Character Rules Alert</h3>
                  <div className="space-y-1">
                    {excludedCharViolations.length > 0 && (
                      <p className="text-yellow-700 text-sm">
                        <span className="font-medium">Avoid using:</span> {topic?.markingRule?.excludedCharacters}
                      </p>
                    )}
                    {includedCharViolations.length > 0 && (
                      <p className="text-yellow-700 text-sm">
                        <span className="font-medium">Required characters:</span> {topic?.markingRule?.includedCharacters}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium text-gray-800">{words}</span> words
                </span>
              </div>
              <div className="h-4 w-px bg-gray-300"></div>
              <span className="text-sm text-gray-600">{input.length} characters</span>
              <div className="h-4 w-px bg-gray-300"></div>
              <span className="text-sm text-gray-600">{typingSpeed} WPM</span>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const text = input.split('\n').map(line => line.trimStart()).join('\n');
                  setInput(text);
                }}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
              >
                Align Text
              </button>
              <button
                onClick={() => setInput('')}
                className="px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors duration-200"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="px-4">
          <button
            className="group relative w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-5 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
            disabled={timeLeft === 0 || input.trim().length === 0}
            onClick={handleAutoSubmit}
          >
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>

            {/* Button content */}
            <div className="relative z-10 flex items-center justify-center gap-3">
              <FaCheckCircle className="text-xl group-hover:scale-110 transition-transform" />
              {timeLeft === 0 ? "Time's Up! Submit Now" : 'Complete & Submit'}
            </div>
          </button>
        </div>

      </div>

      {/* Add custom animations to global styles */}
      <style jsx global>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        /* Custom range slider */
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #3b82f6;
          border: 2px solid white;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }
        
        input[type="range"]::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #3b82f6;
          border: 2px solid white;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default ExamWriting;